import { useState, useEffect, useContext } from 'react';
import { TextField, Grid } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { AppContext } from '../all';

type Facility = {
    id: number;
    name: string;
};

// Normalize text: remove accents and convert to lowercase
const normalizeText = (text: string | null | undefined): string => {
    if (!text) return '';
    return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
};

// Custom filter that ignores accents and case
const customFilterOptions = (
    options: Facility[],
    state: { inputValue: string; getOptionLabel: (option: Facility) => string },
): Facility[] => {
    const normalizedInput = normalizeText(state.inputValue);
    if (!normalizedInput) return options;
    return options.filter((option) =>
        normalizeText(option.name).includes(normalizedInput),
    );
};

type Props = {
    grid?: boolean;
    caption: string;
    defVal: number;
    size?: 'small' | 'medium';
    variant?: 'outlined' | 'filled' | 'standard';
    disabled?: boolean;
    onChange: (facility: Facility | null) => void;
    [key: string]: unknown;
};

// Componente para seleccionar instalaciones mediante autocomplete
export function SttSelectFacility({
    grid,
    caption,
    defVal,
    size = 'small',
    variant = 'outlined',
    disabled,
    onChange,
    ...rest
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const [facilities, setFacilities] = useState<Facility[]>([]);

    // Load data
    function loadFacilities(
        params = { page: 1, rows: 1000, status: 'ACTIVE,ONLY_SC' },
    ) {
        cxt?.api('GET', `/scs/${cxt.sc?.id}/facilities`, {
            params,
            success: (response: { rows: Facility[] }) => {
                setFacilities(response.rows);
            },
        });
    }

    // Init
    useEffect(loadFacilities, []);

    // -----| Render |-----
    const content = (
        <Autocomplete
            disabled={disabled}
            value={
                facilities.find((facility: any) => facility.id === defVal) ||
                null
            }
            options={facilities}
            getOptionLabel={(option: any) => option.name || ''}
            filterOptions={customFilterOptions}
            onChange={(_ev, facility) => onChange(facility)}
            renderInput={(params: any) => (
                <TextField
                    fullWidth
                    {...params}
                    size={size}
                    label={caption}
                    variant={variant}
                />
            )}
        />
    );

    return grid ? <Grid {...rest}>{content}</Grid> : content;
}
